/**
 * Centralized enum value constants.
 *
 * This file provides a single source of truth for enum values used throughout
 * the frontend. These arrays should match the backend enum definitions in
 * backend/hera-api/src/hera_api/db/enums.py
 *
 * IMPORTANT: When backend enum values change, update these arrays accordingly.
 */

import type {
  InspectionStatus,
  LicenseStatus,
} from "@/hera-api/client/types.gen";

/**
 * All valid LicenseStatus values.
 * This array must match the LicenseStatus enum in the backend.
 */
export const ALL_LICENSE_STATUSES = [
  "REQUIRES_PAYMENT",
  "PAY_LATER",
  "PAYMENT_FAILED",
  "PAYMENT_PROCESSING",
  "PAID_PENDING_REVIEW",
  "NON_COMPLIANT_NO_RENEWAL_POSSIBLE",
  "COMPLIANT_UNTIL_RENEWAL_DATE",
  "DEREG_PAYMENT_REQUIRED",
  "DEREG_PAYMENT_PROCESSING",
  "DEREG_PAYMENT_FAILED",
  "DE_REGISTRATION_PENDING",
  "CANCELLED",
  "CANCELLED_REQUIRES_PAYMENT",
  "REJECTED",
  "EXEMPT",
] as const satisfies readonly string[];

export type AppLicenseStatus = LicenseStatus;

/**
 * LicenseStatus values that are valid for editing.
 * This is a subset of ALL_LICENSE_STATUSES.
 */
export const VALID_LICENSE_STATUS_VALUES_FOR_EDIT: readonly LicenseStatus[] = [
  "REQUIRES_PAYMENT",
  "PAY_LATER",
  "PAID_PENDING_REVIEW",
  "NON_COMPLIANT_NO_RENEWAL_POSSIBLE",
  "COMPLIANT_UNTIL_RENEWAL_DATE",
  "DEREG_PAYMENT_REQUIRED",
  "DEREG_PAYMENT_FAILED",
  "DE_REGISTRATION_PENDING",
  "CANCELLED",
  "REJECTED",
] as const satisfies readonly LicenseStatus[];

/**
 * All valid InspectionStatus values.
 * This array must match the InspectionStatus enum in the backend.
 */
export const ALL_INSPECTION_STATUSES: readonly InspectionStatus[] = [
  "REQUIRES_PAYMENT",
  "PAYMENT_FAILED",
  "PAYMENT_PROCESSING",
  "PAID_PENDING_REVIEW",
  "INSPECTION_PENDING",
  "COMMITTED",
  "NON_COMPLIANT",
  "COMPLIANT",
  "CANCELLED",
  "EXEMPT",
] as const satisfies readonly InspectionStatus[];
